// ============================================================
// ANSICHTSSACHE-AGENT – Rückfrage
// ============================================================
// Ersetzt `window.confirm`. Das Systemfenster bringt seine
// eigene Schrift, seine eigenen Knöpfe („OK“) und die Adresse
// der Seite mit — zwischen eigenen Bauteilen sieht es aus wie
// hineingefallen, und es kann nicht sagen, ob etwas gelöscht
// oder nur übernommen wird.
//
// Aufruf von überall, ohne Zustand in der aufrufenden
// Komponente:
//
//   if (!(await confirmDialog("Diesen Brief löschen?"))) return;
//
//   if (!(await confirmDialog({
//     title: "Ticket TK-0016 löschen?",
//     description: "Alle Kommentare verschwinden mit.",
//     confirmLabel: "Löschen",
//     danger: true,
//   }))) return;
//
// `<ConfirmHost />` steht einmal im Wurzel-Layout und zeichnet
// die Rückfrage. Die Funktion oben meldet ihm die Frage an und
// gibt ein Versprechen zurück, das mit der Antwort aufgeht.
// ============================================================

"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";
import {
  Dialog,
  DialogCancel,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSubmit,
  DialogTitle,
} from "./dialog";
import { UeberAllem } from "@/components/ticket-reporter/ueber-allem";

export interface ConfirmOptions {
  /** Die Frage selbst. Eine Zeile, sie steht als Überschrift. */
  title: string;
  /** Was daran hängt: was mit verschwindet, was nicht zurückkommt. */
  description?: string;
  /** Beschriftung des Ja-Knopfes. Benennt die Tat, nicht „OK“. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Rot, wenn etwas verschwindet. */
  danger?: boolean;
}

interface ConfirmRequest extends ConfirmOptions {
  id: number;
  resolve: (answer: boolean) => void;
}

// ------------------------------------------------------------
// Ablage außerhalb von React: die Frage wird aus Ereignis-
// Handlern gestellt, nicht beim Rendern.
// ------------------------------------------------------------
let currentRequest: ConfirmRequest | null = null;
const pending: ConfirmRequest[] = [];
const listeners = new Set<() => void>();
let counter = 0;

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return currentRequest;
}

function notify() {
  for (const listener of listeners) listener();
}

/**
 * Stellt die Rückfrage und wartet auf die Antwort.
 *
 * Kommen zwei Fragen gleichzeitig — ein Doppelklick auf zwei
 * Zeilen —, wird die zweite angestellt statt verworfen; sonst
 * bliebe ihr Versprechen für immer offen und der Aufrufer
 * hinge in seinem `await`.
 */
export function confirmDialog(question: string | ConfirmOptions): Promise<boolean> {
  const options = typeof question === "string" ? { title: question } : question;
  return new Promise<boolean>((resolve) => {
    const request: ConfirmRequest = { ...options, id: ++counter, resolve };
    if (currentRequest) {
      pending.push(request);
      return;
    }
    currentRequest = request;
    notify();
  });
}

function answer(request: ConfirmRequest, value: boolean) {
  // Nur die Frage beantworten, die auch offen ist: ein später
  // eintreffendes Escape darf nicht die nächste wegklicken.
  if (currentRequest?.id !== request.id) return;
  currentRequest = pending.shift() ?? null;
  notify();
  request.resolve(value);
}

/**
 * Zeichnet die offene Rückfrage. Gehört einmal ins Wurzel-Layout,
 * neben `<Toaster />`.
 */
export function ConfirmHost() {
  const request = useSyncExternalStore(subscribe, getSnapshot, () => null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  const id = request?.id;

  /**
   * Der Dialog holt sich den Fokus ein zweites Mal.
   *
   * Die Frage kommt meist aus einem Menü hinter den drei Punkten,
   * und das gibt den Fokus beim Schließen an seinen Auslöser
   * zurück — NACHDEM der Dialog schon offen ist. Der erste Griff
   * des Dialogs geht dabei verloren, der Fokus steht hinter dem
   * Kasten, und die Eingabetaste trifft ins Leere. Der zweite
   * Griff kommt eine Wimper später und gewinnt.
   *
   * Beim Schließen bekommt der Auslöser den Fokus zurück — wie
   * beim Systemfenster, das man ja auch nicht verlassen hat.
   */
  useEffect(() => {
    if (!id) return undefined;
    let vorher: HTMLElement | null = null;
    const nachfassen = setTimeout(() => {
      vorher = document.activeElement as HTMLElement | null;
      confirmButton.current?.focus();
    }, 80);
    return () => {
      clearTimeout(nachfassen);
      if (vorher?.isConnected) vorher.focus();
    };
  }, [id]);

  if (!request) return null;

  // Die Rückfrage liegt ÜBER allem, auch über einem Seitenblatt:
  // sie wird von dort aus gestellt (Person löschen, Gruppe
  // auflösen), und bis zum 24.09.2026 lag sie darunter — hinter
  // der Abdunklung des Blatts, ohne Zeiger, das Blatt fing den
  // Fokus zurück. `UeberAllem` hängt sie an den Rumpf und gibt ihr
  // Zeiger, Fokus und Escape zurück (Begründung dort).
  return (
    <UeberAllem fokus onEscape={() => answer(request, false)}>
      <Dialog
        // Der Schlüssel setzt den Dialog je Frage neu auf — sonst
        // behielte die zweite Frage den Fokus-Stand der ersten.
        key={request.id}
        open
        onClose={() => answer(request, false)}
        className="max-w-md"
      >
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            answer(request, true);
          }}
        >
          <DialogHeader>
            <DialogTitle>{request.title}</DialogTitle>
            {request.description && <DialogDescription>{request.description}</DialogDescription>}
          </DialogHeader>
          <DialogFooter className="mt-3">
            {/* Dieselben Knöpfe wie in jedem Modal. „Abbrechen“ geht
              über den Abbrechen-Weg des Dialogs, also `onClose`. */}
            <DialogCancel>{request.cancelLabel ?? "Abbrechen"}</DialogCancel>
            {/* `submit`: der Dialog setzt den Fokus beim Öffnen auf
              das erste Feld oder den Absende-Knopf — damit liegt
              er hier, und die Eingabetaste bestätigt. */}
            <DialogSubmit ref={confirmButton} variant={request.danger ? "danger" : "default"}>
              {request.confirmLabel ?? (request.danger ? "Löschen" : "Fortfahren")}
            </DialogSubmit>
          </DialogFooter>
        </form>
      </Dialog>
    </UeberAllem>
  );
}
